import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router'; // Import ActivatedRoute
import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { PasswordService } from '@app/finxone-web-frontend/app/lib/services/password-service/password-service.service';
import {
  BaseCssProperties,
  BaseWidgetProperties,
  ProviderClass,
  WidgetNames,
  baseCssConfigStyle,
  PasswordFieldConfigurations,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { FormActionTypeEnum } from '../../../../../../../../../libs/shared/enums/form-action.enum';
import {
  FormStage,
  MultiPartFormHolderService,
} from '../../../../../lib/services/multipart-holder/multipart-form-holder.service';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';
import { FormSubmitter } from '../../form-widgets/form-submitter/form-submitter.component';
import { map } from 'rxjs';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-password-change',
  templateUrl: './password-change.component.html',
})
export class PasswordChangeComponent extends FormSubmitter implements OnInit {
  public token: string;
  public fieldType = 'password';
  public buttonContent = '';
  public renderedButtonContent = '';
  public zone = '';
  public nextPageUrl: string;
  public passwordForm: FormGroup;

  public reviewDetailsButtonStyle: baseCssConfigStyle = {};
  private inputActiveStyle: baseCssConfigStyle;
  private inputInactiveStyle: baseCssConfigStyle;
  public inputStyle: baseCssConfigStyle;
  public colorTextAlignStyle: baseCssConfigStyle;
  public inputContentStyle: baseCssConfigStyle;
  public mergedHeadingStyle: baseCssConfigStyle;

  public passwordFieldConfigs: PasswordFieldConfigurations = {} as any;

  constructor(
    private _router: Router,
    private route: ActivatedRoute,
    public pageConfig: PageConfigurationProvider,
    public dataIn: ProviderClass<any>,
    private configService: ConfigService,
    public override store: Store,
    public formHolderService: MultiPartFormHolderService,
    public passwordService: PasswordService,
    private formSubmissionService: FormSubmissionService,
    private urlZoneInput: ZonePageUrlProvider,
  ) {
    super(store);
    this.colorTextAlignStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_BOLD,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_UNDERLINE,
      BaseCssProperties.COLOR,
      BaseCssProperties.TEXT_FONT_STYLE,
      BaseCssProperties.TEXTALIGN,
    ]);
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.BORDER,
      BaseCssProperties.BACKGROUND_COLOR,
    ]);

    this.inputContentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
    ]);
    this.mergedHeadingStyle = { ...this.colorTextAlignStyle, ...this.inputContentStyle };
    this.getAndAssignInputFieldStyles(this.dataIn.attri.widgetProperties);
    this.zone = this.urlZoneInput.zoneUrlInput;
    this.fetchPasswordFieldConfig();
  }

  private fetchPasswordFieldConfig() {
    this.configService
      .getPasswordFieldConfigurations()
      .pipe(
        // assign config; if nullish leave as empty object so template can default
        map((config) => {
          this.passwordFieldConfigs = config ?? ({} as PasswordFieldConfigurations);
        }),
      )
      .subscribe();
  }

  private getAndAssignInputFieldStyles(widgetProperties: BaseWidgetProperties) {
    const { baseStyle, activeStyle, inactiveStyle } =
      this.configService.getInputFieldCssStyles(widgetProperties);
    this.inputActiveStyle = {
      ...baseStyle,
      ...activeStyle,
    };
    this.inputInactiveStyle = {
      ...inactiveStyle,
      ...activeStyle,
    };
    this.inputStyle = this.inputInactiveStyle;
  }

  ngOnInit(): void {
    this.createFormStage();
    this.buildForm();

    this.route.queryParams?.subscribe((params) => {
      this.token = params['token'];
    });

    this.buttonContent =
      this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton']?.widgetProperties.textContent ??
      'Change Password';
  }

  private createFormStage(): void {
    const formStage = new FormStage({
      position: 0,
      name: WidgetNames.PASSWORD_CHANGE,
      route: '',
      form: new FormGroup({
        currentPassword: new FormControl('', Validators.required),
      }),
    });
    this.formSubmissionService.addCurrentPageFormGroups(formStage.form);
    this.formHolderService.config.addStage(formStage);
  }

  private buildForm(): void {
    const stage = this.formHolderService.config.getStage(WidgetNames.PASSWORD_CHANGE);
    this.passwordForm = stage.getForm();
  }

  public onSubmit(noRedirect = false): void {
    if (this.passwordForm.valid) {
      const formValues: Record<string, any> = this.passwordForm.value;

      this.formSubmissionService
        .submitIndividualForm(
          formValues,
          noRedirect ? '' : '/zones/profile/password-check-email',
          FormActionTypeEnum.PASWORD_CHANGE,
        )
        .then((success) => {
          if (success) {
            let name = '';
            Object.values(formValues).forEach((value: string) => {
              name = name + value + ' ';
            });
          } else {
            return;
          }
        });
    } else {
      this.passwordForm.markAllAsTouched();
      // Assign empty value to mark the change password form as invalid
      this.formSubmissionService.submitIndividualForm(
        this.passwordForm.value,
        '',
        FormActionTypeEnum.PASWORD_CHANGE,
      );
    }
  }

  public forgotPassword(): void {
    this.passwordForm.markAllAsTouched();

    this.passwordService.forgotPasswordEmail().subscribe({
      next: (success) => {
        if (success) {
          this._router.navigate(['/zones/profile/password-check-email']);
        } else {
          return;
        }
      },
    });
  }

  public updateInputFieldCurrentStyles(event: boolean) {
    this.inputStyle = event ? this.inputActiveStyle : this.inputInactiveStyle;
  }

  public changeFieldType(fieldType: 'password' | 'text'): void {
    this.fieldType = fieldType == 'text' ? 'password' : 'text';
  }
}
